import { motion } from 'framer-motion';
import { useState, useEffect, useRef } from 'react';
import './Tour.css';
import tourLifetimeImg from './files/Tour/compressed-logo.png';
import tourCompressedMobileImg from './files/Tour/compressed-logo.png';
import vanLargeImg from './files/Tour/van_large.png';
import shawnPortraitImg from './files/SY_Port.jpg';
import shawnSignature from './files/shawn_signature.svg';
import USAMap from './USAMap';
import stopPhotos from './stopPhotos';
import tourData from '../data/tourStops.json';

function Tour({ skipIntro = false }) {
  const getIsMobile = () => typeof window !== 'undefined' && window.innerWidth <= 1400;
  const [isMobile, setIsMobile] = useState(() => getIsMobile());
  const [activeMobileTab, setActiveMobileTab] = useState('launch');
  const tabPanelRef = useRef(null);
  // Skip intro animation on mobile since we show a different layout
  const [introComplete, setIntroComplete] = useState(() => skipIntro || getIsMobile());
  const launchStops = tourData.tourStops.filter((stop) => stop.type === 'launch');
  const tourStops = tourData.tourStops.filter((stop) => stop.type === 'stop');

  const handleMobileTabChange = (tab) => {
    setActiveMobileTab(tab);
    requestAnimationFrame(() => {
      const container = document.querySelector('.tour.section-container');
      const panel = tabPanelRef.current;
      if (!container || !panel) {
        return;
      }
      if (container.scrollHeight <= container.clientHeight) {
        return;
      }
      const containerRect = container.getBoundingClientRect();
      const panelRect = panel.getBoundingClientRect();
      const targetTop = panelRect.top - containerRect.top + container.scrollTop - 20;
      container.scrollTo({ top: targetTop, behavior: 'smooth' });
    });
  };

  useEffect(() => {
    const handleResize = () => {
      const mobile = getIsMobile();
      setIsMobile(mobile);
      // Auto-complete intro on mobile
      if (mobile && !introComplete) {
        setIntroComplete(true);
      }
    };
    // Check on mount to ensure correct state
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [introComplete]);

  useEffect(() => {
    const className = 'tour-mobile-full';
    const root = document.documentElement;
    const body = document.body;
    if (isMobile) {
      root.classList.add(className);
      body.classList.add(className);
    } else {
      root.classList.remove(className);
      body.classList.remove(className);
    }
    return () => {
      root.classList.remove(className);
      body.classList.remove(className);
    };
  }, [isMobile]);

  return (
    <motion.section
      className="tour section-container"
      initial={{ opacity: 1 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, x: -100 }}
      transition={{ duration: 0.6 }}
    >
      <motion.div
        className="tour-bg"
        initial={{ opacity: skipIntro ? 0.5 : 0 }}
        animate={{ opacity: 0.5 }}
        transition={skipIntro ? { duration: 0 } : { duration: 1.2, ease: 'easeOut' }}
      />
      <motion.div
        className="tour-logo"
        style={{
          position: 'absolute',
          left: '0px',
          top: '0px',
          transform: 'translateX(-118px) translateY(-8px) scale(0.55)'
        }}
        initial={{ opacity: 1 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0 }}
        onAnimationComplete={() => {
          if (!introComplete && !skipIntro) {
            setTimeout(() => setIntroComplete(true), 300);
          }
        }}
      >
        <motion.img
          src={tourLifetimeImg}
          alt="Tour of a Lifetime"
          initial={isMobile ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.85 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={isMobile ? { duration: 0 } : { duration: 0.7, ease: 'easeOut' }}
        />
      </motion.div>

      {introComplete && (
        <motion.div
          className="tour-content"
          initial={{ opacity: skipIntro ? 1 : 0, scale: skipIntro ? 1 : 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={skipIntro ? { duration: 0 } : { delay: 0.3, duration: 0.6, ease: 'easeOut' }}
        >
          {isMobile ? (
            <>
              <div className="tour-mobile-image">
                <img src={tourCompressedMobileImg} alt="Tour of a Lifetime" />
              </div>
              <div className="tour-mobile-titles">
                <h2 className="tour-mobile-title">"Tell Them Today"</h2>
                <div className="tour-mobile-subtitle">Summer 2026</div>
              </div>
              <div className="tour-mobile-tabs" role="tablist" aria-label="Tour sections">
                <button
                  type="button"
                  className={`tour-mobile-tab ${activeMobileTab === 'launch' ? 'is-active' : ''}`}
                  onClick={() => handleMobileTabChange('launch')}
                >
                  Launch
                </button>
                <button
                  type="button"
                  className={`tour-mobile-tab ${activeMobileTab === 'dates' ? 'is-active' : ''}`}
                  onClick={() => handleMobileTabChange('dates')}
                >
                  Tour
                </button>
                <button
                  type="button"
                  className={`tour-mobile-tab ${activeMobileTab === 'about' ? 'is-active' : ''}`}
                  onClick={() => handleMobileTabChange('about')}
                >
                  About
                </button>
              </div>
              <div ref={tabPanelRef} className="tour-mobile-tab-panel">
                {activeMobileTab === 'launch' && (
                  <div className="tour-photo-list">
                    {launchStops.map((stop) => (
                      <div key={stop.id} className="tour-photo-card">
                        <img
                          src={stopPhotos[stop.id]}
                          alt={`${stop.name} in ${stop.city}, ${stop.state}`}
                          loading="lazy"
                        />
                        <span className="tour-photo-caption">{stop.name}</span>
                        <span className="tour-photo-subcaption">
                          {stop.city}, {stop.state} - {stop.date}
                        </span>
                        {stop.flickrUrl && (
                          <a
                            className="tour-photo-album-btn"
                            href={stop.flickrUrl}
                            target="_blank"
                            rel="noreferrer"
                          >
                            View the Photo Album
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {activeMobileTab === 'dates' && (
                  <div className="tour-photo-list">
                    {tourStops.map((stop) => (
                      <div key={stop.id} className="tour-photo-card">
                        <img
                          src={stopPhotos[stop.id]}
                          alt={`Tour of a Lifetime stop in ${stop.city}, ${stop.state}`}
                          loading="lazy"
                        />
                        <span className="tour-photo-caption">
                          {stop.city}, {stop.state}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                {activeMobileTab === 'about' && (
                  <div className="tour-mobile-about">
                    <img src={shawnPortraitImg} alt="Shawn York portrait" />
                    <p>
                      In 2026, I celebrated two milestones: 20 years with Chick-fil-A and the
                      release of my first book, Love Works Here. To celebrate, my family and I
                      hit the road over the summer to thank the leaders who have inspired,
                      mentored, and challenged me along the way.
                    </p>
                    <p>
                      At each stop, I had the chance to "Tell Them Today"- to share gratitude
                      in-person with those who helped make my journey possible. It wasn't just a
                      fun tour of a lifetime - but truly, the tour of MY lifetime.
                    </p>
                    <img src={shawnSignature} alt="Shawn York signature" className="tour-signature" />
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="tour-map-container">
                <motion.div
                  className="tour-map-wrapper"
                  initial={{ opacity: skipIntro ? 1 : 0 }}
                  animate={{ opacity: 1 }}
                  transition={skipIntro ? { duration: 0 } : { delay: 0.05, duration: 0.45, ease: 'easeOut' }}
                >
                  <USAMap
                    skipIntro={skipIntro}
                    vanImage={
                      <div className="tour-van-wrapper">
                        <motion.img
                          src={vanLargeImg}
                          alt="Tour Van"
                          className="tour-van"
                          initial={{ opacity: skipIntro ? 1 : 0 }}
                          animate={{ opacity: 1 }}
                          transition={skipIntro ? { duration: 0 } : { delay: 0.3, duration: 0.6 }}
                        />
                      </div>
                    }
                  />
                </motion.div>
              </div>
              <motion.div
                className="tour-story"
                initial={{ opacity: skipIntro ? 1 : 0, y: skipIntro ? 0 : 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={skipIntro ? { duration: 0 } : { delay: 0.5, duration: 0.5, ease: 'easeOut' }}
              >
                <img src={shawnPortraitImg} alt="Shawn York portrait" />
                <p>
                  In 2026, I celebrated two milestones: 20 years with Chick-fil-A and the
                  release of my first book, Love Works Here. To celebrate, my family and I hit
                  the road over the summer to thank the leaders who have inspired, mentored,
                  and challenged me along the way.
                </p>
                <p>
                  At each stop, I had the chance to "Tell Them Today"- to share gratitude
                  in-person with those who helped make my journey possible. It wasn't just a fun
                  tour of a lifetime - but truly, the tour of MY lifetime.
                </p>
                <img src={shawnSignature} alt="Shawn York signature" className="tour-signature" />
              </motion.div>
            </>
          )}
        </motion.div>
      )}
    </motion.section>
  );
}

export default Tour;

